import React from 'react';
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from "../../stores/store";
import { observer } from "mobx-react-lite";

/**
 * PublicHeader - Navigation header for anonymous/public users
 * Similar to TopHeader but with public-appropriate menu items
 */
function PublicHeader() {
    const { mainStore } = useStore();
    const { t, i18n } = useTranslation();
    const location = useLocation();

    const isHome = location.pathname === '/' || location.pathname === '/Home';
    const isToolsCatalog = location.pathname === '/ToolsCatalog';
    const isToolSelection = location.pathname === '/ToolSelection';
    const isAbout = location.pathname === '/About';
    const isHelp = location.pathname === '/Help';
    const isTools = location.pathname.startsWith('/Tools');

    const closeMenu = () => {
        const menu = document.getElementById("publicHeaderMenu");
        if (menu && menu.classList.contains("show")) {
            menu.classList.remove("show");
        }
    };

    const setLanguage = (lang) => {
        i18n.changeLanguage(lang);
        mainStore.setLang(lang);
    };

    return (
        <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm">
            <div className="container-fluid px-3 px-lg-4">
                {/* Logo */}
                <Link className="navbar-brand fw-bold" to="/Home">
                    <img
                        src="/assets/img/OnlineCADCOM_FullLogo.png"
                        alt="Online-CADCOM"
                        style={{ height: '40px' }}
                    />
                </Link>

                {/* Mobile Toggle */}
                <button
                    className="navbar-toggler border-0"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#publicHeaderMenu"
                    aria-controls="publicHeaderMenu"
                    aria-expanded="false"
                    aria-label="Toggle navigation"
                >
                    <span className="navbar-toggler-icon"></span>
                </button>

                <div className="collapse navbar-collapse" id="publicHeaderMenu">
                    {/* Main Navigation - Left side */}
                    {/* Order: Home, About, Tools Catalog, Tool Selection, Help */}
                    <ul className="navbar-nav me-auto mb-2 mb-lg-0">
                        {/* Home link */}
                        <li className="nav-item">
                            <Link
                                to="/Home"
                                className={`nav-link px-3 ${isHome ? "active fw-semibold" : ""}`}
                                onClick={closeMenu}
                                style={{ color: isHome ? '#112d4e' : '#495057' }}
                            >
                                <i className="bi bi-house-door me-1"></i>
                                {t("home")}
                            </Link>
                        </li>

                        {/* About link */}
                        <li className="nav-item">
                            <Link
                                to="/About"
                                className={`nav-link px-3 ${isAbout ? "active fw-semibold" : ""}`}
                                onClick={closeMenu}
                                style={{ color: isAbout ? '#112d4e' : '#495057' }}
                            >
                                <i className="bi bi-info-circle me-1"></i>
                                {t("about")}
                            </Link>
                        </li>

                        {/* Tools Catalog link */}
                        <li className="nav-item">
                            <Link
                                to="/ToolsCatalog"
                                className={`nav-link px-3 ${isToolsCatalog ? "active fw-semibold" : ""}`}
                                onClick={closeMenu}
                                style={{ color: isToolsCatalog ? '#112d4e' : '#495057' }}
                            >
                                <i className="bi bi-tools me-1"></i>
                                {t("toolsCatalog")}
                            </Link>
                        </li>

                        {/* Tool Selection link */}
                        <li className="nav-item">
                            <Link
                                to="/ToolSelection"
                                className={`nav-link px-3 ${isToolSelection ? "active fw-semibold" : ""}`}
                                onClick={closeMenu}
                                style={{ color: isToolSelection ? '#112d4e' : '#495057' }}
                            >
                                <i className="bi bi-sliders me-1"></i>
                                {t("toolSelection")}
                            </Link>
                        </li>

                        {/* Help link */}
                        <li className="nav-item">
                            <Link
                                to="/Help"
                                className={`nav-link px-3 ${isHelp ? "active fw-semibold" : ""}`}
                                onClick={closeMenu}
                                style={{ color: isHelp ? '#112d4e' : '#495057' }}
                            >
                                <i className="bi bi-question-circle me-1"></i>
                                {t("help")}
                            </Link>
                        </li>
                    </ul>

                    {/* Right side - Language & Auth */}
                    <ul className="navbar-nav align-items-lg-center">
                        {/* Language Dropdown with Flags */}
                        <li className="nav-item dropdown">
                            <a
                                className="nav-link dropdown-toggle px-3 d-flex align-items-center"
                                href="#"
                                id="dropdownLanguage"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                style={{ color: '#495057' }}
                            >
                                {/* Current language flags */}
                                <span className="d-flex align-items-center">
                                    {i18n.language === "lng1" && (
                                        <span className="d-flex align-items-center" style={{ gap: "2px" }}>
                                            <img src="https://flagcdn.com/w20/gb.png" alt="UK" style={{ height: "14px", borderRadius: "2px" }} />
                                            <img src="https://flagcdn.com/w20/us.png" alt="USA" style={{ height: "14px", borderRadius: "2px" }} />
                                        </span>
                                    )}
                                    {i18n.language === "lng2" && (
                                        <img src="https://flagcdn.com/w20/bg.png" alt="Bulgaria" style={{ height: "14px", borderRadius: "2px" }} />
                                    )}
                                    {i18n.language === "lng3" && (
                                        <span className="d-flex align-items-center" style={{ gap: "2px" }}>
                                            <img src="https://flagcdn.com/w20/al.png" alt="Albania" style={{ height: "14px", borderRadius: "2px" }} />
                                            <img src="https://flagcdn.com/w20/xk.png" alt="Kosovo" style={{ height: "14px", borderRadius: "2px" }} />
                                        </span>
                                    )}
                                </span>
                                <span className="d-lg-none ms-2">{t("language")}</span>
                            </a>
                            <ul
                                className="dropdown-menu dropdown-menu-end shadow-sm border-0"
                                aria-labelledby="dropdownLanguage"
                                style={{ minWidth: '180px' }}
                            >
                                {/* English - UK & USA flags */}
                                <li>
                                    <a
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setLanguage("lng1");
                                            closeMenu();
                                        }}
                                        className={`dropdown-item py-2 d-flex align-items-center ${i18n.language === "lng1" ? "active bg-light fw-semibold" : ""}`}
                                    >
                                        <span className="d-flex align-items-center me-2" style={{ gap: "3px", minWidth: "44px" }}>
                                            <img
                                                src="https://flagcdn.com/w20/gb.png"
                                                srcSet="https://flagcdn.com/w40/gb.png 2x"
                                                alt="UK"
                                                style={{ height: "16px", borderRadius: "2px", boxShadow: "0 0 1px rgba(0,0,0,0.2)" }}
                                            />
                                            <img
                                                src="https://flagcdn.com/w20/us.png"
                                                srcSet="https://flagcdn.com/w40/us.png 2x"
                                                alt="USA"
                                                style={{ height: "16px", borderRadius: "2px", boxShadow: "0 0 1px rgba(0,0,0,0.2)" }}
                                            />
                                        </span>
                                        <span>{t("languageLNG1")}</span>
                                        <i
                                            className="bi bi-check2 ms-auto"
                                            style={{
                                                visibility: i18n.language === "lng1" ? "visible" : "hidden",
                                                color: "#112d4e"
                                            }}
                                        ></i>
                                    </a>
                                </li>

                                {/* Bulgarian - Bulgarian flag */}
                                <li>
                                    <a
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setLanguage("lng2");
                                            closeMenu();
                                        }}
                                        className={`dropdown-item py-2 d-flex align-items-center ${i18n.language === "lng2" ? "active bg-light fw-semibold" : ""}`}
                                    >
                                        <span className="d-flex align-items-center me-2" style={{ minWidth: "44px" }}>
                                            <img
                                                src="https://flagcdn.com/w20/bg.png"
                                                srcSet="https://flagcdn.com/w40/bg.png 2x"
                                                alt="Bulgaria"
                                                style={{ height: "16px", borderRadius: "2px", boxShadow: "0 0 1px rgba(0,0,0,0.2)" }}
                                            />
                                        </span>
                                        <span>{t("languageLNG2")}</span>
                                        <i
                                            className="bi bi-check2 ms-auto"
                                            style={{
                                                visibility: i18n.language === "lng2" ? "visible" : "hidden",
                                                color: "#112d4e"
                                            }}
                                        ></i>
                                    </a>
                                </li>

                                {/* Albanian - Albania & Kosovo flags */}
                                <li>
                                    <a
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setLanguage("lng3");
                                            closeMenu();
                                        }}
                                        className={`dropdown-item py-2 d-flex align-items-center ${i18n.language === "lng3" ? "active bg-light fw-semibold" : ""}`}
                                    >
                                        <span className="d-flex align-items-center me-2" style={{ gap: "3px", minWidth: "44px" }}>
                                            <img
                                                src="https://flagcdn.com/w20/al.png"
                                                srcSet="https://flagcdn.com/w40/al.png 2x"
                                                alt="Albania"
                                                style={{ height: "16px", borderRadius: "2px", boxShadow: "0 0 1px rgba(0,0,0,0.2)" }}
                                            />
                                            <img
                                                src="https://flagcdn.com/w20/xk.png"
                                                srcSet="https://flagcdn.com/w40/xk.png 2x"
                                                alt="Kosovo"
                                                style={{ height: "16px", borderRadius: "2px", boxShadow: "0 0 1px rgba(0,0,0,0.2)" }}
                                            />
                                        </span>
                                        <span>{t("languageLNG3")}</span>
                                        <i
                                            className="bi bi-check2 ms-auto"
                                            style={{
                                                visibility: i18n.language === "lng3" ? "visible" : "hidden",
                                                color: "#112d4e"
                                            }}
                                        ></i>
                                    </a>
                                </li>
                            </ul>
                        </li>

                        {/* Vertical Divider - Desktop only */}
                        <li className="nav-item d-none d-lg-flex align-items-center mx-2">
                            <span style={{
                                width: '1px',
                                height: '24px',
                                backgroundColor: '#dee2e6'
                            }}></span>
                        </li>

                        {/* Auth Buttons Container */}
                        <li className="nav-item d-flex align-items-center gap-2 mt-3 mt-lg-0 mb-2 mb-lg-0">
                            {/* Login Button - Outlined style */}
                            <Link
                                to="/login"
                                className="btn btn-outline-primary btn-sm px-3 py-2"
                                onClick={closeMenu}
                                style={{
                                    borderColor: '#112d4e',
                                    color: '#112d4e',
                                    fontWeight: '500',
                                    borderRadius: '6px',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#112d4e';
                                    e.currentTarget.style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#112d4e';
                                }}
                            >
                                <i className="bi bi-box-arrow-in-right me-1"></i>
                                {t("login")}
                            </Link>

                            {/* Register Button - Filled style */}
                            <Link
                                to="/register"
                                className="btn btn-sm px-3 py-2"
                                onClick={closeMenu}
                                style={{
                                    backgroundColor: '#112d4e',
                                    color: '#fff',
                                    fontWeight: '500',
                                    borderRadius: '6px',
                                    border: '2px solid #112d4e',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#1a4066';
                                    e.currentTarget.style.borderColor = '#1a4066';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#112d4e';
                                    e.currentTarget.style.borderColor = '#112d4e';
                                }}
                            >
                                <i className="bi bi-person-plus me-1"></i>
                                {t("register")}
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
        </nav>
    );
}

export default observer(PublicHeader);
